import { useMutation, useQueryClient } from '@tanstack/react-query'
import { releaseSchools } from '@/api/group/group.service'
import { reloadGroupsWithSchoolsAndTanks } from '@/utils/loadStorage'
import type { ReleaseSchoolsCommand } from '@/types/domain'

interface RemoveGroupParams {
  groupId: string
  schoolIds: string[]
}

/**
 * Hook for removing tanks (schools) from a group
 *
 * Flow:
 * 1. Call release-schools API with schoolIds
 * 2. Reload all group/tank data
 * 3. Removed tanks become ungrouped
 */
export const useRemoveGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ groupId, schoolIds }: RemoveGroupParams) => {
      if (schoolIds.length === 0) {
        throw new Error('No schools selected for removal')
      }

      const command: ReleaseSchoolsCommand = {
        groupId,
        schoolIds,
        reason: 'Removed via Remove from Group modal',
      }

      await releaseSchools(command)

      // Reload data after successful removal
      await reloadGroupsWithSchoolsAndTanks()

      return { groupId, removedCount: schoolIds.length }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['tanks'] })
    },
  })
}
